import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Probes whether Stripe can load in the buyer's browser BEFORE we hand off to
// the hosted checkout page. On a CLEAR block (ad-blocker/VPN), reveals a
// non-blocking warning banner and beacons the event so support gets visibility
// even if the buyer abandons. Fails silent on ambiguous/slow results — never
// cries wolf, never blocks the Pay button.
export default class extends Controller {
  static targets = ["banner"]
  static values = {
    enabled: Boolean,
    reportUrl: String,
    timeoutMs: { type: Number, default: 4000 }
  }

  connect() {
    if (!this.enabledValue) return
    this.run()
  }

  async run() {
    const result = await this.probeStripe()
    if (!result.blocked) return
    this.reveal()
    this.beacon(result.detail)
  }

  // Resolves { blocked, detail }. "blocked" is true ONLY on a definitive load
  // error (not a timeout), so a slow network doesn't trigger a false warning.
  probeStripe() {
    return new Promise((resolve) => {
      const script = document.createElement("script")
      script.src = "https://js.stripe.com/v3/"
      const timer = setTimeout(() => {
        cleanup()
        resolve({ blocked: false }) // ambiguous -> stay silent
      }, this.timeoutMsValue)
      const cleanup = () => { clearTimeout(timer); script.remove() }
      script.onload = () => { cleanup(); resolve({ blocked: false }) }
      script.onerror = () => { cleanup(); resolve({ blocked: true, detail: "js.stripe.com" }) }
      document.head.appendChild(script)
    })
  }

  reveal() {
    if (this.hasBannerTarget) this.bannerTarget.classList.remove("d-none")
  }

  async beacon(detail) {
    if (!this.reportUrlValue) return
    try {
      await post(this.reportUrlValue, { body: { detail } })
    } catch (_e) {
      // best-effort; visibility loss here is acceptable, never block checkout
    }
  }
}
